import { Article } from '../types';

export const nercNetBillingRegulations2026: Article = {
    id: 'nerc-net-billing-regulations-2026-guide',
    title: 'NERC Net Billing Regulations 2026: Selling Solar Power to the Grid',
    slug: 'nerc-net-billing-regulations-2026-solar-grid-export',
    excerpt: 'Complete guide to NERC\'s Net Billing Regulations 2026. Learn how commercial, industrial, and estate solar users can legally export excess power to DisCos.',
    category: 'Renewable Energy',
    date: 'June 13, 2026',
    readTime: '6 min read',
    author: 'power.ng Research',
    trending: true,
    keywords: ['NERC Net Billing 2026', 'sell solar power to grid Nigeria', 'net metering Nigeria 2026', 'solar energy export grid', 'bidirectional smart meter'],
    metaDescription: 'NERC Net Billing Regulations 2026 explained. Learn how commercial, industrial, and estate prosumers in Nigeria can sell excess solar power back to DisCos.',
    faqs: [
        {
            question: 'What is the difference between Net Billing and Net Metering in Nigeria?',
            answer: 'Under net metering, you offset your grid usage on a 1-to-1 kWh basis. Under net billing, you pay the retail tariff for power imported from the grid, while the energy you export is credited to your bill at a lower, NERC-approved export tariff.'
        },
        {
            question: 'Who is eligible for the NERC Net Billing scheme in 2026?',
            answer: 'Eligible users (prosumers) must have a renewable energy installation (primarily solar PV) with a capacity between 50 kWp and 1.5 MWp connected to a licensed DisCo\'s network.'
        },
        {
            question: 'Can I sell solar power back to the grid and receive cash?',
            answer: 'No. Exported solar energy generates billing credits that offset your future grid consumption bills. Credits cannot be converted into cash, and your bill cannot go below zero.'
        },
        {
            question: 'How is the value of exported solar electricity determined?',
            answer: 'NERC applies a 0.55 factor to the wholesale cost of electricity for off-peak (daytime) exports, and a 0.75 factor for peak exports (6:00 PM – 9:00 PM), which require battery verification.'
        }
    ],
    content: `## Quick Summary: Can You Sell Solar Energy to the Grid in Nigeria?
Yes. Under the newly enacted **NERC Net Billing Regulations 2026** (Regulation No. NERC-R-002-2026), eligible electricity consumers who generate renewable energy (like solar) can export their surplus power back to the local Distribution Company (DisCo) in exchange for billing credits that reduce their monthly electricity bills.

---

## 1. What is the NERC Net Billing Scheme?

In early June 2026, the Nigerian Electricity Regulatory Commission (NERC) officially commenced the **Net Billing Regulations 2026**. Designed under the provisions of the Electricity Act 2023, this framework governs how private prosumers—consumers who also produce electricity—can connect their systems to the grid and monetize excess power.

### Net Metering vs. Net Billing
It is crucial to understand that NERC has implemented a **Net Billing** system, not a Net Metering system:

*   **Net Metering (1:1 Swap):** If you consume 100 kWh from the grid and export 100 kWh of solar energy, they cancel each other out completely.
*   **Net Billing (Asymmetrical Pricing):** You buy power from the grid at the retail rate (e.g., Band A prices), but when you export surplus solar power, the DisCo credits you at a lower, wholesale-based rate determined by NERC.

---

## 2. Who is Eligible to Participate?

The Net Billing scheme is not currently open to small-scale residential consumers. NERC has targeted the regulations at commercial, industrial, and cooperative/estate prosumers.

**Eligibility Requirements:**
*   **Technology:** Renewable energy sources, primarily Solar PV systems.
*   **System Capacity:** Must have an installed capacity between **50 Kilowatts peak (kWp) and 1.5 Megawatts peak (MWp)**.
*   **Connection:** The prosumer's facility must be physically connected to a licensed DisCo's distribution network.
*   **Compliance:** System installations must be certified by the Nigerian Electricity Management Services Agency (NEMSA).

---

## 3. How the Pricing and Bill Credits Work

Because the Net Billing rate is lower than the retail tariff rate, the system is designed to encourage prosumers to consume their solar power first, exporting only the "wasted" surplus.

### The Export Tariff Formulas
NERC determines the price of exported energy using specific adjustment factors multiplied by the average wholesale cost of electricity on the grid:

| Export Window | Time Period | Credit Multiplier | Verification Required |
| :--- | :--- | :--- | :--- |
| **Off-Peak (Daytime)** | 8:00 AM – 5:00 PM | **0.55** of wholesale cost | Standard bidirectional meter |
| **Peak (Evening)** | 6:00 PM – 9:00 PM | **0.75** of wholesale cost | Verified battery storage system |

> [!IMPORTANT]
> **No Cash Payouts:** You cannot get cash back from your DisCo. The credits can only be used to offset your future grid imports. If your net bill calculation is negative (meaning you exported more value than you imported), the credit rolls over to the next month. Your monthly cash bill is capped at a minimum of ₦0.00.

---

## 4. How to Apply and Get Connected

To legally export energy under the Net Billing scheme, prosumers must follow a formal application and engineering inspection workflow:

### Step 1: Request a Feasibility Study
Apply to your local DisCo (e.g., Ikeja Electric, Eko DisCo, AEDC) requesting grid interconnection. The DisCo will perform a technical feasibility study to ensure the local feeder and transformer can safely handle power feedback without voltage spikes.

### Step 2: Sign the Net Billing Agreement
Once the engineering review is successful, you will sign a standard Net Billing Interconnection Agreement with the DisCo. This contract defines the export tariff, safety guidelines, and system capacities.

### Step 3: Install a Bidirectional Smart Meter
You must purchase and install an approved **bidirectional smart meter**. Unlike standard prepaid meters, a bidirectional meter has two registers:
1.  **Register 1 (Import):** Measures electricity flowing from the grid to your facility.
2.  **Register 2 (Export):** Measures solar electricity flowing from your facility back to the grid.

### Step 4: NEMSA Certification & Commissioning
Before powering up, NEMSA inspectors must visit and verify that the system is equipped with an **auto-disconnect switch (anti-islanding protection)**. This ensures that if there is a grid blackout, your solar system stops exporting energy immediately so that utility workers fixing the grid are not electrocuted.

---

## 5. Is Net Billing Financially Viable in 2026?

For businesses, estates, and factories running large solar PV arrays, Net Billing is a game-changer. 

Previously, if a factory was closed on weekends, the solar energy generated on Saturday and Sunday was wasted unless they invested in massive, expensive battery banks. Under the 2026 regulations, that weekend surplus can be fed directly back to the grid, earning credits that substantially lower the business's grid electricity bills during high-production weekdays.

While the export rate is discounted (0.55/0.75 factor), it significantly shortens the payback period of C&I solar investments in Nigeria, making commercial solar projects more lucrative than ever before.`
};
